import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { signInWithPopup } from "firebase/auth";
import { googleProvider, facebookProvider } from "../firebase";
import "./SignUp.css";

/* Firebase */
import {
  getAuth,
  createUserWithEmailAndPassword,
  signInAnonymously,
  sendEmailVerification,
  signOut,
} from "firebase/auth";
import {
  doc,
  setDoc,
  getDoc,
  serverTimestamp,
  setLogLevel,
} from "firebase/firestore";
import { db } from "../firebase";
import { query, collection, where, getDocs } from "firebase/firestore";

if (process.env.NODE_ENV !== "production") setLogLevel("debug");
const FUNCTIONS_BASE = process.env.REACT_APP_FUNCTIONS_URL || "";

export default function SignUp({ fixedRole }) {
  const params      = useParams();
  const initialRole =
    fixedRole || (params["*"]?.toLowerCase() === "kid" ? "Kid" : "Parent");

  // +++ 新增 Kid 帳號暱稱 state
  const [accountName, setAccountName] = useState("");

  const [firstName, setFirstName]   = useState("");
  const [lastName,  setLastName]    = useState("");
  const [birthDate, setBirthDate]   = useState("");
  const [email,     setEmail]       = useState("");
  const [password,  setPassword]    = useState("");
  const [parentCode,setParentCode]  = useState("");
  const [accountType,setAccountType]= useState(initialRole);   // Kid | Parent
  const [error,     setError]       = useState("");
  const [acceptedDisclaimer, setAcceptedDisclaimer] = useState(false);
  const [showDisclaimer, setShowDisclaimer] = useState("");
  const navigate = useNavigate();
  const auth     = getAuth();

  const genSixDigits = () =>
    Math.floor(100000 + Math.random() * 900000).toString();

  const parentCodeExists = async (code) =>
    (await getDoc(doc(db, "parentCodes", code))).exists();

  // 家長 email 通知
  const sendParentCodeEmail = async (to, code) => {
    const payload = {
      to,
      subject: "Your Koala Quest Parent Code",
      html: `<p style="font-family:sans-serif;font-size:1rem;line-height:1.5">Hi there!<br><br>Your Koala Quest parent code is <strong style="font-size:1.25rem">${code}</strong>.<br>Share this code with your child so they can link to your account.<br><br>Log in at <a href="https://koalaquest.ca">koalaquest.ca</a> any time to view progress.<br><br>— Koala Quest Team</p>`
    };
    try {
      await fetch(`${FUNCTIONS_BASE}/api/sendEmail`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
    } catch (err) {
      console.error("Could not send parent‑code email", err);
    }
  };

const isAccountNameTaken = async (name, code) => {
  const q = query(
    collection(db, "users"),
    where("accountName", "==", name),
    where("parentCode", "==", code)
  );
  const snap = await getDocs(q);
  return !snap.empty;
};

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    // --- 驗證
    if (accountType === "Parent") {
      if (!firstName || !lastName || !birthDate || !email || !password)
        return setError("All fields are required.");
    }
    if (accountType === "Kid") {
      if (!firstName || !lastName || !birthDate || !accountName || !parentCode || !password)
        return setError("All fields are required.");

      // ===== 新增唯一性檢查 =====
if (await isAccountNameTaken(accountName.trim(), parentCode.trim())) {
  return setError("This account name is already taken for this parent code. Try another.");
}

    }
    if (password.length < 6)
      return setError("Password must be at least 6 characters.");

    let code = parentCode.replace(/\s/g, "");

    if (accountType === "Parent") {
      if (!code) code = genSixDigits();
      if (!/^\d{6}$/.test(code))
        return setError("Parent Code must be 6 digits.");
      if (await parentCodeExists(code))
        return setError("That parent code is already taken — try another.");
    } else {
      if (!/^\d{6}$/.test(code))
        return setError("Parent Code must be exactly 6 digits.");
      if (!auth.currentUser) await signInAnonymously(auth);
      if (!(await parentCodeExists(code)))
        return setError("Parent code not found. Double-check with your parent.");
    }

    try {
  if (auth.currentUser?.isAnonymous) await auth.currentUser.delete();

  // === 1️⃣ 建立 Auth 使用者 ===
  let userEmail = email;
  if (accountType === "Kid") {
    // kid 不用 email，用暱稱+parent code 虛擬 email
    userEmail = `kid-${accountName}-${code}@koalaquest.ca`;
  }
  const cred = await createUserWithEmailAndPassword(auth, userEmail, password);
  const uid  = cred.user.uid;

  // === 2️⃣ Email 驗證只對 parent 做，kid 不寄信
  if (accountType === "Parent") {
    sendEmailVerification(cred.user).catch((err) => {
      console.warn("email verification failed", err);
    });
  }

  // === 3️⃣ Firestore 寫入 ===
  await setDoc(doc(db, "users", uid), {
    firstName, lastName,
    birthDate,
    accountName: accountType === "Parent" ? email : accountName,
    parentCode:  code,
    accountType,
    coins: 0,
    createdAt:     serverTimestamp(),
    lastLoginTime: serverTimestamp(),
  });

  if (accountType === "Parent") {
    await setDoc(doc(db, "parentCodes", code), {
      parentUid: uid,
      createdAt: serverTimestamp(),
    });
    sendParentCodeEmail(email, code);
  }

  alert(
    accountType === "Parent"
      ? `Sign-up successful! Your parent code is ${code}. (We've also emailed it to you.)`
      : "Sign-up successful!"
  );
  // *** Only sign out here, if kid ***
  if (accountType === "Kid") await signOut(auth);

  navigate("/login");
} catch (err) {
  setError(err.message.replace("Firebase:", "").trim());
}
  };

  const handleSocialSignup = async (provider) => {
  setError("");
  try {
    const cred = await signInWithPopup(auth, provider);
    const uid = cred.user.uid;
    const snap = await getDoc(doc(db, "users", uid));

    if (!snap.exists()) {
      // 🔹 新帳號：產生 parent code，寫入資料
      let code = genSixDigits();
      while (await parentCodeExists(code)) {
        code = genSixDigits();
      }

      await setDoc(doc(db, "users", uid), {
        firstName: cred.user.displayName?.split(" ")[0] || "",
        lastName:  cred.user.displayName?.split(" ")[1] || "",
        birthDate: "",
        accountName: cred.user.email,
        parentCode: code,
        accountType: "Parent",
        coins: 0,
        createdAt: serverTimestamp(),
        lastLoginTime: serverTimestamp(),
      });

      await setDoc(doc(db, "parentCodes", code), {
        parentUid: uid,
        createdAt: serverTimestamp(),
      });

      sendParentCodeEmail(cred.user.email, code);
      alert(`Sign-up successful! Your parent code is ${code}. (We've also emailed it to you.)`);
    }

    navigate("/ParentsDashboard", { replace: true });
  } catch (err) {
    console.error(err);
    setError("Social signup failed. You may already have an account.");
  }
};

// === UI ===
  return (
    <div className="signup-page-container">
      <div className="signup-form-container">
        <h2 className="signup-form-title">
          {accountType === "Kid" ? "Create Kid Account" : "Create Parent Account"}
        </h2>
        {error && <p className="signup-form-error">{error}</p>}

        <form onSubmit={handleSubmit}>
          {/* 角色選擇，不動 */}
          {!fixedRole && !params["*"] && (
            <div className="signup-role-container">
              <p className="signup-role-heading">Choose Account Type:</p>
              {['Kid','Parent'].map((role) => (
                <label key={role} className="signup-role-label">
                  <input
                    type="radio"
                    name="role"
                    value={role}
                    checked={accountType === role}
                    onChange={(e) => setAccountType(e.target.value)}
                  /> {role}
                </label>
              ))}
            </div>
          )}

          {/* === 新增兩欄式內容開始 === */}
          <div className="signup-form-fields">
            {/* 左欄 */}
            <div className="signup-form-left">
              <div className="signup-input-container">
                <input value={firstName} onChange={(e)=>setFirstName(e.target.value)} required />
                <label>First Name</label>
              </div>
              <div className="signup-input-container">
                <input value={lastName} onChange={(e)=>setLastName(e.target.value)} required />
                <label>Last Name</label>
              </div>
              <div className="signup-input-container">
                <input type="date" value={birthDate} onChange={(e)=>setBirthDate(e.target.value)} required />
                <label className="signup-label-date">Birth Date</label>
              </div>
              {accountType === "Parent" && (
                <div className="signup-input-container">
                  <input value={email} onChange={(e)=>setEmail(e.target.value)} autoComplete="username" required />
                  <label>Email</label>
                </div>
              )}
              {accountType === "Kid" && (
                <div className="signup-input-container">
                  <input value={accountName} onChange={(e)=>setAccountName(e.target.value)} required autoComplete="username" />
                  <label>Account Name (Kid Username)</label>
                </div>
              )}
            </div>

            {/* 右欄 */}
            <div className="signup-form-right">
              <div className="signup-input-container">
                <input type="password" value={password} onChange={(e)=>setPassword(e.target.value)} required autoComplete="new-password" />
                <label>Password</label>
              </div>
              <div className="signup-input-container">
                <input
                  maxLength={6}
                  value={parentCode}
                  onChange={(e)=>setParentCode(e.target.value)}
                  required={accountType === "Kid"}
                />
                <label>
                  {accountType === "Kid"
                    ? "Enter Parent's 6-Digit Code"
                    : "Create / Choose 6-Digit Parent Code (leave blank for random)"}
                </label>
              </div>
              {/* 折疊式 Disclaimer */}
              <div className="signup-disclaimer-collapsible">
                <input
                  type="checkbox"
                  id="disclaimer"
                  checked={acceptedDisclaimer}
                  onChange={e => setAcceptedDisclaimer(e.target.checked)}
                  required
                  style={{marginRight:"0.7em"}}
                />
                <label htmlFor="disclaimer" style={{cursor: "pointer"}} onClick={() => setShowDisclaimer(v => !v)}>
                  <strong>Disclaimer:</strong>
                  <span className="disclaimer-toggle-link">（點我閱讀詳細條款）</span>
                </label>
                {showDisclaimer && (
                  <div className="disclaimer-content">
                    Koala Quest is an interactive, gamified academic education website for Grades 1–12 students. By signing up, you confirm you are a student or a parent/guardian creating an account for your child.<br />
                    You agree not to share personal information (such as home address or phone number) in our community, and agree to our <a href="/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a> and <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>. If you are under 13, please have a parent or guardian assist with your registration.
                  </div>
                )}
              </div>
              <button type="submit" className="signup-submit-button">
                Sign Up
              </button>
            </div>
          </div>
          {/* === 兩欄式內容結束 === */}
        </form>

        {accountType === "Parent" && (
          <div style={{ marginTop: 24 }}>
            <hr />
            <p style={{ textAlign: "center", margin: "12px 0", fontWeight: "bold" }}>
              Or sign up with:
            </p>
            <button
              onClick={() => handleSocialSignup(googleProvider)}
              className="guest-social-login google"
            >
              Google
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
